import { FilePondFile } from "filepond";
import { FGCustomPanel, FGListen, FGMaskInput, FGSelectInput, FGTextInput } from "nsitools-react";
import * as React from "react";
import { FilePond } from "react-filepond";
import * as Yup from "yup";

import {
  CharacteristicApi,
  DogApi,
  DogHealthTestApi,
  EHealthValueType,
  SaveDogCharacteristicDto,
} from "../../../../../api";
import { SmallFormGenerator } from "../../../../../components";
import { useGlobalData } from "../../../../../contexts";
import { useApiService, useSaveApi, useTl } from "../../../../../hooks";
import { useReferential } from "../../../../../hooks/useReferential";
import { ETLCodes } from "../../../../../locales";

export interface IDogHealthTestPanelProps {
  dogId: number;
  onSaved: () => void;
}

export const DogHealthTestPanel: React.FunctionComponent<IDogHealthTestPanelProps> = ({ dogId, onSaved }) => {
  const { t } = useTl();
  const api = useApiService(DogHealthTestApi);
  const charApi = useApiService(CharacteristicApi);
  const dogApi = useApiService(DogApi);
  const [characteristicFile, setCharacteristicFile] = React.useState<FilePondFile>(null);

  const [valueRequest, setValueRequest] = React.useState<number>();
  const [healthValueTypeCode, setHealthValueTypeCode] = React.useState<EHealthValueType>();

  const [values, vloading] = useReferential((a) => a.getHealthValues(valueRequest), false, [valueRequest]);
  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      characteristicId: Yup.number()
        .nullable()
        .required(t(ETLCodes.Required))
        .test(
          "is-unique-transient-char",
          t(ETLCodes.ThisCharacteristicIsAwaitingValidationInDashboard),
          async function (value) {
            if (!value || !this.parent.characteristicValueId) return true;
            const res = await api.isTestUniqueTransient(dogId, value, this.parent.characteristicValueId);
            return res.value;
          }
        )
        .test("is-unique-char", t(ETLCodes.CharacteristicAndValueAlreadyExistsForDog), async function (value) {
          if (!value || !this.parent.characteristicValueId) return true;
          const res = await api.isTestUnique(dogId, value, this.parent.characteristicValueId);
          return res.value;
        }),
      numericValue: Yup.number()
        .nullable()
        .test("required", t(ETLCodes.Required), function (value) {
          if (this.parent.alphaValue === null && this.parent.healthValueId === null) {
            return value !== null;
          }
          return true;
        }),
      alphaValue: Yup.string()
        .nullable()
        .test("required", t(ETLCodes.Required), function (value) {
          if (this.parent.numericValue === null && this.parent.healthValueId === null) {
            return value !== null;
          }
          return true;
        }),
      healthValueId: Yup.number()
        .nullable()
        .test("required", t(ETLCodes.Required), function (value) {
          if (this.parent.alphaValue === null && this.parent.numericValue === null) {
            return value !== null;
          }
          return true;
        })
        .test("is-unique-char-type", t(ETLCodes.CharacteristicAndValueAlreadyExistsForDog), async function (value) {
          if (!value || !this.parent.characteristicId) return true;
          const res = await api.isTestUnique(dogId, this.parent.characteristicId, value);
          return res.value;
        })
        .test(
          "is-unique-char-type-transient",
          t(ETLCodes.ThisCharacteristicIsAwaitingValidationInDashboard),
          async function (value) {
            if (!value || !this.parent.characteristicId) return true;
            const res = await api.isTestUniqueTransient(dogId, this.parent.characteristicId, value);
            return res.value;
          }
        ),
    });
  }, [api, dogId, t]);
  const { currentLanguage } = useGlobalData();

  const [characteristics, cloading] = useReferential(
    (a) => a.getCharacteristics(false, currentLanguage.languageId),
    false,
    []
  );

  const { saving, saveItem } = useSaveApi<SaveDogCharacteristicDto>(
    React.useMemo(
      () => ({
        saveApiFn: (data) =>
          dogApi.addCharacteristic(
            dogId,
            data.characteristicId,
            data.healthValueId,
            data.numericValue,
            data.alphaValue,
            characteristicFile ? { data: characteristicFile.file, fileName: characteristicFile.filename } : null
          ),
        onSaveFn: () => onSaved(),
      }),
      [dogApi, characteristicFile, dogId, onSaved]
    )
  );
  const initialData = React.useMemo(
    () =>
      new SaveDogCharacteristicDto({
        dogId: dogId,
        alphaValue: null,
        numericValue: null,
        healthValueId: null,
      }).toJSON(),
    [dogId]
  );

  return (
    <SmallFormGenerator
      onSubmit={saveItem}
      saving={saving}
      initialValues={initialData}
      editMode={true}
      validationSchema={FormSchema}
      minLabelWidth={110}
    >
      <FGListen
        field="characteristicId"
        onChanged={async (value) => {
          setValueRequest(value);
          setHealthValueTypeCode(await charApi.getHealthValueTypeCode(value));
        }}
      />
      <FGCustomPanel>
        {(ctx) => (
          <FilePond
            labelIdle={t(ETLCodes.FilePondDragOrSelect)}
            onupdatefiles={(f) => {
              ctx.formik.setFieldValue("characteristicFile", f.length > 0 ? f[0] : null);
              setCharacteristicFile(f.length > 0 ? f[0] : null);
            }}
          />
        )}
      </FGCustomPanel>
      <FGSelectInput
        name="characteristicId"
        items={characteristics}
        loading={cloading}
        label={t(ETLCodes.Characteristic)}
      />
      {healthValueTypeCode === EHealthValueType.ValueList ? (
        <FGSelectInput name="healthValueId" items={values} loading={vloading} label={t(ETLCodes.Value)} />
      ) : healthValueTypeCode === EHealthValueType.Numeric ? (
        <FGMaskInput name="numericValue" label={t(ETLCodes.NumericValue)} cleaveOptions={{ numericOnly: true }} />
      ) : (
        <FGTextInput name="alphaValue" label={t(ETLCodes.Value)} />
      )}
    </SmallFormGenerator>
  );
};
